/**
 * @file 사내 게시판 공통 값·헬퍼
 *
 * 게시판 화면 4개(홈·목록·상세·작성)가 같은 톤을 쓰도록 색·서식·아이콘을 한곳에 모았다.
 * 색은 전부 {@code ds.colors} 토큰에서 유도한다 — 브랜드 색을 바꾸면 게시판도 함께 따라온다.
 */
import React from "react";
import dayjs from "dayjs";
import { ds } from "../../styles/designSystem";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import SlideshowRoundedIcon from "@mui/icons-material/SlideshowRounded";
import FolderZipRoundedIcon from "@mui/icons-material/FolderZipRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";

export const C = ds.colors;

/**
 * 게시판 카드·목록 헤더 바 색.
 *
 * <p>시안의 짙은 슬레이트를 우리 토큰({@code textSecondary})으로 대체했다 — 새 hex 를 들이면
 * 브랜드 색을 바꿀 때 이 한 곳이 남아 따로 튄다.
 */
export const BOARD_HEADER_BG = C.textSecondary;

/** 공지 행 배경 — primary 계열의 가장 옅은 톤. 굵은 글씨와 함께 '고정' 임을 알린다. */
export const NOTICE_ROW_BG = C.primaryBg;

/** 목록 날짜 — 시안과 같은 {@code YY-MM-DD}. 게시판은 연도까지 보이는 편이 이력 판단에 낫다. */
export const formatBoardDate = (v) => (v ? dayjs(v).format("YY-MM-DD") : "");

/** 상세 헤더 날짜 — 분 단위까지. */
export const formatBoardDateTime = (v) => (v ? dayjs(v).format("YY-MM-DD HH:mm") : "");

/** 댓글 시간 — 상세 안에서만 쓰여 연도를 줄여도 맥락이 유지된다. */
export const formatCommentTime = (v) => (v ? dayjs(v).format("YY-MM-DD HH:mm") : "");

/**
 * 확장자·MIME 로 파일 종류 아이콘을 고른다.
 *
 * <p>시안처럼 <b>PDF 는 빨강, 엑셀은 초록, 압축은 노랑</b>으로 색을 달리한다 — 첨부가 여러 개일 때
 * 이름을 읽지 않고도 무엇이 들었는지 눈에 들어오게 하려는 것이다.
 */
export function fileTypeIcon(att, size = 18) {
    const name = (att?.originalFileName || "").toLowerCase();
    const mime = (att?.contentType || "").toLowerCase();
    const ext = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1) : "";
    const sx = (color) => ({ fontSize: size, color, flexShrink: 0 });

    if (mime.includes("pdf") || ext === "pdf") return <PictureAsPdfRoundedIcon sx={sx("#dc2626")} />;
    if (mime.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext))
        return <ImageRoundedIcon sx={sx(C.primary)} />;
    if (mime.includes("spreadsheet") || mime.includes("ms-excel") || ["xlsx", "xls", "xlsm", "csv", "ods"].includes(ext))
        return <TableChartRoundedIcon sx={sx("#16a34a")} />;
    if (mime.includes("presentation") || mime.includes("powerpoint") || ["pptx", "ppt", "odp"].includes(ext))
        return <SlideshowRoundedIcon sx={sx("#ea580c")} />;
    if (mime.includes("word") || ["docx", "doc", "odt", "rtf", "hwp", "hwpx"].includes(ext))
        return <DescriptionRoundedIcon sx={sx("#2563eb")} />;
    if (["zip", "7z", "rar", "tar", "gz"].includes(ext)) return <FolderZipRoundedIcon sx={sx("#d97706")} />;
    return <InsertDriveFileRoundedIcon sx={sx(C.textMuted)} />;
}

/** 첨부 목록 상단의 "총 N개(합계 용량)" 표시용 바이트 합. */
export const totalBytes = (list = []) => list.reduce((sum, a) => sum + (a.fileSize || 0), 0);

/** 검색 범위 — 서버 {@code BoardPostService.SearchScope} 와 값이 일치해야 한다. */
export const SEARCH_SCOPES = [
    { value: "ALL", label: "전체" },
    { value: "TITLE", label: "제목" },
    { value: "AUTHOR", label: "작성자" },
];

/** 목록 행 hover — 클릭 가능함을 알리는 최소 신호(가로 구분선을 쓰지 않는 대신). */
export const ROW_HOVER_SX = {
    cursor: "pointer",
    transition: "background-color 0.12s",
    "&:hover": { bgcolor: C.bg },
};
